import React, { useContext } from "react";
import DropdownMenu from "./DropdownMenu";

import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import TimeRecordEditDialog from "../components/TimeRecordEditDialog";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";

import moreIcon from "../assets/icon_more.svg";
import deleteIcon from "../assets/icon_delete.svg";
import editIcon from "../assets/icon_edit.svg";

// Helper function to format time in "hh:mm:ss" format
const formatTime = (ms) => {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);

  return [hours, minutes, seconds]
    .map((val) => (val < 10 ? `0${val}` : val))
    .join(":");
};

// Helper function to format date
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const TaskTimeRecordItem = ({ record, actionLabel = "", isAi = false }) => {
  const { deleteTimeRecord, updateTimeRecord } = useContext(FirebaseContext);
  const { openModal, closeModal } = useCommonUI();
  const handleEditTimeRecord = () => {
    const spentHours = Math.floor(record.spentTime / (1000 * 60 * 60));
    const spentMinutes = Math.floor(
      (record.spentTime % (1000 * 60 * 60)) / (1000 * 60)
    );
    const spentSeconds = Math.floor((record.spentTime % (1000 * 60)) / 1000);
    openModal(
      <TimeRecordEditDialog
        initialHours={spentHours}
        initialMinutes={spentMinutes}
        initialSeconds={spentSeconds}
        initialStartedTime={record.startedTime}
        initialTrackerKind={record.trackerKind === "ai" ? "ai" : "human"}
        showAiSwitch={isAiInvoiceEnabled()}
        onUpdate={(updated) => {
          const updatedSpentTime =
            updated.hours * 3600000 +
            updated.minutes * 60000 +
            updated.seconds * 1000;

          updateTimeRecord(record.id, {
            spentTime: updatedSpentTime,
            startedTime: updated.startedTime,
            trackerKind: updated.trackerKind,
          });
          closeModal(); // Close the modal after updating the time record
        }}
        onClose={closeModal} // Pass closeModal as the onClose handler
      />
    );
  };
  const menuItems = [
    {
      icon: editIcon,
      label: "Edit Time Record",
      onClick: () => {
        handleEditTimeRecord();
      },
    },
    {
      icon: deleteIcon,
      label: "Delete Time Record",
      onClick: () => {
        deleteTimeRecord(record.id);
      },
    },
  ];

  return (
    <div style={styles.container}>
      {/* Left: start date, with the tracked action's label beneath it (Android
          parity) when this record was produced by tracking a specific action. */}
      <div style={styles.leftCol}>
        <p style={styles.date}>{formatDate(record.startedTime)}</p>
        {actionLabel ? (
          <p style={styles.actionLabel}>{actionLabel}</p>
        ) : null}
      </div>
      <p style={styles.time}>{formatTime(record.spentTime)}</p>
      <div style={styles.rightCol}>
        {isAi ? <span style={styles.aiBadge}>AI</span> : null}
        <DropdownMenu icon={moreIcon} menuItems={menuItems} alignRight={true} />
      </div>
    </div>
  );
};

// Styles for the component
const styles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // Right inset keeps the "…" button clear of Safari's floating overlay
    // scrollbar, which otherwise sits on top of it and swallows taps until it
    // fades. The bottom border still spans the full row width.
    padding: "16px 16px 16px 0",
    borderBottom: "1px solid var(--border)", // Thin border to separate items
  },
  leftCol: {
    flex: 1, // occupy the left space; stacks date + action label
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  date: {
    fontSize: "16px",
    color: "var(--text)",
    margin: 0,
  },
  actionLabel: {
    fontSize: "12px",
    color: "var(--text-faint)",
    margin: "2px 0 0 0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  time: {
    fontSize: "16px",
    color: "var(--text)",
    marginRight: "16px", // Add some space between time and more icon
  },
  rightCol: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  aiBadge: {
    fontSize: "10px",
    fontWeight: 600,
    color: "var(--track-ai, #3b82f6)",
    border: "1px solid var(--track-ai, #3b82f6)",
    backgroundColor: "rgba(59, 130, 246, 0.12)",
    borderRadius: "999px",
    padding: "1px 8px",
    lineHeight: 1.6,
    whiteSpace: "nowrap",
  },
  moreIcon: {
    cursor: "pointer",
    fontSize: "24px",
    color: "var(--text)",
  },
};

export default TaskTimeRecordItem;
